import { useEffect, useState } from "react";
import { Cookie, Settings, Check, X, ChevronLeft } from "lucide-react";
import { resolveApiBaseUrl } from "@/utils/tracking";

const STORAGE_KEY = "selena_cookie_consent_v1";

const defaultPrefs = {
  necessary: true, // always
  analytics: false,
  marketing: false,
  ts: null,
};

function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return { ...defaultPrefs, ...parsed };
  } catch {
    return null;
  }
}

function savePrefs(prefs) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...prefs, necessary: true, ts: new Date().toISOString() })
    );
  } catch {
    /* ignore */
  }
}

function trackCookieConsent(action, prefs) {
  const apiBaseUrl = resolveApiBaseUrl({ forTracking: false });
  if (!apiBaseUrl) return;

  const payload = {
    action,
    necessary: true,
    analytics: !!prefs.analytics,
    marketing: !!prefs.marketing,
    ts: new Date().toISOString(),
    lang: document.documentElement.lang || "ro",
  };

  fetch(`${apiBaseUrl}/cookie-consent.php`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {
    // Cookie tracking must never block consent UX.
  });
}

export default function CookieBanner({ t, openSignal }) {
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);

  // First load: open if no consent saved
  useEffect(() => {
    const existing = loadPrefs();
    if (existing) {
      setPrefs(existing);
    } else {
      setOpen(true);
    }
  }, []);

  // External open trigger from footer button
  useEffect(() => {
    if (openSignal > 0) {
      const existing = loadPrefs();
      if (existing) setPrefs(existing);
      setShowSettings(true);
      setOpen(true);
    }
  }, [openSignal]);

  const acceptAll = () => {
    const p = { necessary: true, analytics: true, marketing: true };
    setPrefs(p);
    savePrefs(p);
    trackCookieConsent("accept_all", p);
    setOpen(false);
    setShowSettings(false);
  };

  const rejectAll = () => {
    const p = { necessary: true, analytics: false, marketing: false };
    setPrefs(p);
    savePrefs(p);
    trackCookieConsent("reject_all", p);
    setOpen(false);
    setShowSettings(false);
  };

  const saveCustom = () => {
    savePrefs(prefs);
    trackCookieConsent("custom", prefs);
    setOpen(false);
    setShowSettings(false);
  };

  const toggle = (k) => setPrefs((p) => ({ ...p, [k]: !p[k] }));

  if (!open) return null;

  const c = t.cookies;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-[80] p-3 sm:p-5 pointer-events-none"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-title"
        className="pointer-events-auto max-w-5xl mx-auto bg-white border-2 border-[#0b1220] shadow-2xl"
      >
        {!showSettings ? (
          /* Concise banner view */
          <div className="p-5 sm:p-6 lg:p-7">
            <div className="flex items-start gap-4">
              <div className="hidden sm:flex w-11 h-11 bg-[#eaf0fb] items-center justify-center flex-shrink-0">
                <Cookie className="w-5 h-5 text-[#0b378c]" strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0">
                <h2
                  id="cookie-title"
                  data-testid="cookie-title"
                  className="font-[Barlow_Condensed] font-bold uppercase text-2xl text-[#0b1220] tracking-tight leading-tight"
                >
                  {c.title}
                </h2>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-3xl">
                  {c.lead}
                </p>
                <div className="mt-5 flex flex-col sm:flex-row sm:flex-wrap gap-2">
                  <button
                    data-testid="cookie-accept-all"
                    onClick={acceptAll}
                    className="inline-flex items-center justify-center gap-2 bg-[#0b378c] hover:bg-[#082a6e] text-white font-bold uppercase tracking-wider text-xs px-5 py-3 transition-colors"
                  >
                    <Check className="w-4 h-4" />
                    {c.acceptAll}
                  </button>
                  <button
                    data-testid="cookie-reject-all"
                    onClick={rejectAll}
                    className="inline-flex items-center justify-center gap-2 bg-[#0b1220] hover:bg-[#1f2937] text-white font-bold uppercase tracking-wider text-xs px-5 py-3 transition-colors"
                  >
                    <X className="w-4 h-4" />
                    {c.rejectAll}
                  </button>
                  <button
                    data-testid="cookie-settings-open"
                    onClick={() => setShowSettings(true)}
                    className="inline-flex items-center justify-center gap-2 border-2 border-[#0b1220] hover:bg-[#0b1220] hover:text-white text-[#0b1220] font-bold uppercase tracking-wider text-xs px-5 py-3 transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    {c.settings}
                  </button>
                </div>
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  {c.legal}
                </p>
              </div>
            </div>
          </div>
        ) : (
          /* Granular settings view */
          <div className="p-5 sm:p-6 lg:p-7 max-h-[80vh] overflow-y-auto">
            <button
              data-testid="cookie-back"
              onClick={() => setShowSettings(false)}
              className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 hover:text-[#0b378c] mb-4 transition-colors"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              {c.backLink}
            </button>
            <h2 className="font-[Barlow_Condensed] font-bold uppercase text-2xl text-[#0b1220] tracking-tight leading-tight">
              {c.settings}
            </h2>
            <p className="mt-2 text-sm text-slate-600 max-w-3xl">{c.lead}</p>

            <div className="mt-6 grid gap-3">
              <Category
                testid="cat-necessary"
                title={c.categories.necessary.title}
                desc={c.categories.necessary.desc}
                checked={true}
                disabled
                badge={c.categories.necessary.required}
              />
              <Category
                testid="cat-analytics"
                title={c.categories.analytics.title}
                desc={c.categories.analytics.desc}
                checked={prefs.analytics}
                onChange={() => toggle("analytics")}
              />
              <Category
                testid="cat-marketing"
                title={c.categories.marketing.title}
                desc={c.categories.marketing.desc}
                checked={prefs.marketing}
                onChange={() => toggle("marketing")}
              />
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-2">
              <button
                data-testid="cookie-save"
                onClick={saveCustom}
                className="inline-flex items-center justify-center gap-2 bg-[#e3191b] hover:bg-[#c01416] text-white font-bold uppercase tracking-wider text-xs px-5 py-3 transition-colors"
              >
                <Check className="w-4 h-4" />
                {c.save}
              </button>
              <button
                data-testid="cookie-accept-all-2"
                onClick={acceptAll}
                className="inline-flex items-center justify-center gap-2 bg-[#0b378c] hover:bg-[#082a6e] text-white font-bold uppercase tracking-wider text-xs px-5 py-3 transition-colors"
              >
                {c.acceptAll}
              </button>
              <button
                data-testid="cookie-reject-all-2"
                onClick={rejectAll}
                className="inline-flex items-center justify-center gap-2 border-2 border-[#0b1220] hover:bg-[#0b1220] hover:text-white text-[#0b1220] font-bold uppercase tracking-wider text-xs px-5 py-3 transition-colors"
              >
                {c.rejectAll}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Category({ testid, title, desc, checked, onChange, disabled, badge }) {
  return (
    <div
      data-testid={testid}
      className="border border-[#e2e6ec] bg-[#f7f8fa] p-4 flex items-start justify-between gap-4"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-[Barlow_Condensed] font-bold uppercase text-base text-[#0b1220] tracking-tight">
            {title}
          </h3>
          {badge && (
            <span className="font-mono text-[9px] uppercase tracking-[0.2em] bg-[#0b378c] text-white px-2 py-0.5">
              {badge}
            </span>
          )}
        </div>
        <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">{desc}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={onChange}
        className={`relative flex-shrink-0 w-11 h-6 transition-colors ${
          disabled
            ? "bg-[#0b378c] cursor-not-allowed opacity-70"
            : checked
            ? "bg-[#0b378c]"
            : "bg-slate-300 hover:bg-slate-400"
        }`}
      >
        <span
          className={`absolute top-0.5 ${
            checked ? "left-[22px]" : "left-0.5"
          } w-5 h-5 bg-white shadow transition-all`}
        />
      </button>
    </div>
  );
}
